import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import Link from 'next/link';
import { useRouter } from 'next/router';
import { pathOr } from 'ramda';

import { useUser } from '../../../../utils/auth-client';
import { User } from '../../../../types';
import useQueryParams from '../../../../hooks/useQueryParams';
import NewLabel from '../../../../components/NewLabel';
import { getWishlistUrl } from '../../../../utils/wishlistUrls';

type VisibleFn = (user: User | null) => boolean;

const requiresUserWithReferPermission = pathOr(false, ['can_refer']);
const alwaysVisible: VisibleFn = () => true;

type NavLink = {
  title: string | React.ReactNode;
  link: string;
  visible: VisibleFn;
  external?: boolean;
  onClick?: (e: React.MouseEvent) => void;
};

const NavigationWrapper = styled.div`
  background-color: #fbfbfa;

  .authenticated-navigation-tabs {
    height: 60px;
    width: 100%;
    display: flex;
    justify-content: center;
    align-items: center;

    a {
      &.active {
        color: #020204;
        border-bottom: 3px solid;
      }
      padding: 1rem 0;
      flex-grow: 1;
      text-decoration: none;
      color: #6f7a82;
      text-align: center;
      &:hover {
        color: rgba(#6f7a82, 0.6);
        border-bottom: 3px solid;
      }
    }
  }
`;

const Navigation = () => {
  const { currentUser } = useUser();
  const router = useRouter();
  const { query } = useQueryParams();
  const [loading, setLoading] = useState(false);

  const handleWishlistClick = async (e: React.MouseEvent) => {
    e.preventDefault();
    setLoading(true);
    try {
      const url = await getWishlistUrl();
      window.open(url, '_blank', 'noopener,noreferrer');
    } catch (error) {
      console.error('Error opening wishlist:', error);
    } finally {
      setLoading(false);
    }
  };

  const userDashboardNavigationItems: NavLink[] = [
    {
      link: 'my-kiddos',
      title: 'My Kiddos',
      visible: alwaysVisible,
    },
    {
      link: 'orders',
      title: 'Orders',
      visible: alwaysVisible,
    },
    {
      link: '#',
      title: (
        <>
          {loading ? 'Loading...' : 'Wishlists'}
          <NewLabel />
        </>
      ),
      visible: alwaysVisible,
      external: true,
      onClick: handleWishlistClick,
    },
    {
      link: 'referrals',
      title: 'My Referrals',
      visible: requiresUserWithReferPermission,
    },
    {
      link: 'account',
      title: 'Info',
      visible: alwaysVisible,
    },
  ];

  const tabItems = userDashboardNavigationItems
    .filter(tabItem => tabItem.visible(currentUser))
    .map(tabItem => {
      if (tabItem.onClick) {
        return (
          <a key={tabItem.link} href={tabItem.link} onClick={tabItem.onClick}>
            {tabItem.title}
          </a>
        );
      }

      const route = tabItem.external ? tabItem.link : `/dashboard/${tabItem.link}`;
      return tabItem.external ? (
        <a key={tabItem.link} href={route} target="_blank" rel="noopener noreferrer">
          {tabItem.title}
        </a>
      ) : (
        <Link key={tabItem.link} href={{ pathname: route, query }}>
          <a className={router.pathname.startsWith(route) ? 'active' : ''}>{tabItem.title}</a>
        </Link>
      );
    });

  return (
    <NavigationWrapper>
      <div className="authenticated-navigation-tabs">{tabItems}</div>
    </NavigationWrapper>
  );
};

export default Navigation;
